import { DOCUMENT, NgClass } from '@angular/common';
import {
    Component,
    ElementRef,
    HostListener,
    Inject,
    OnDestroy,
    ViewChild,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AiIconComponent } from 'app/shared/icons/ai-icon/ai-icon.component';
import { TranslatePipe } from '@ngx-translate/core';
import { SlateActionExecutor } from './slate-action.executor';
import { SlateIntentEngine } from './slate-intent.engine';
import { SlateSessionService } from './slate-session.service';
import { SlatePrefsService } from './slate-prefs.service';
import { SlateConfigService } from './slate-config.service';
import { SlateMessage, SlateResponseCard, SlateChip, slateChipLabel, slateChipPrompt } from './slate.types';

@Component({
    selector: 'slate-panel',
    templateUrl: './slate-panel.component.html',
    styleUrls: ['./slate-panel.component.scss'],
    encapsulation: ViewEncapsulation.None,
    exportAs: 'slatePanel',
    standalone: true,
    imports: [
        NgClass,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        AiIconComponent,
        TranslatePipe,
    ],
})
export class SlatePanelComponent implements OnDestroy {
    @ViewChild('thread') threadRef?: ElementRef<HTMLDivElement>;
    @ViewChild('inputEl') inputEl?: ElementRef<HTMLTextAreaElement>;

    opened = false;
    busy = false;
    draft = '';
    suggestions: string[] = [];
    roleSubtitle = '';
    favorites: string[] = [];
    detailMode: 'short' | 'detailed' = 'short';
    showPalette = false;
    slowHint = false;
    private _histIndex = -1;
    private _slowTimer?: ReturnType<typeof setTimeout>;

    private readonly _engine = inject(SlateIntentEngine);
    private readonly _executor = inject(SlateActionExecutor);
    private readonly _session = inject(SlateSessionService);
    private readonly _prefs = inject(SlatePrefsService);
    private readonly _config = inject(SlateConfigService);
    private readonly _router = inject(Router);
    private readonly _unsubscribeAll = new Subject<void>();
    private _overlay?: HTMLElement;

    constructor(@Inject(DOCUMENT) private _document: Document) {
        this.refreshChrome();
    }

    get messages(): Array<SlateMessage> {
        return this._session.messages;
    }

    get stickyBits(): string[] {
        const e = this._session.context.lastEntities;
        if (!e) return [];
        const bits: string[] = [];
        if (e.filmTitle || e.filmTitleQuery) {
            bits.push(`Film: ${e.filmTitle || e.filmTitleQuery}`);
        }
        if (e.dateRange?.label && !e.dateRange.inferredDefault) {
            bits.push(`Date: ${e.dateRange.label}`);
        }
        if (e.compareDateRange?.label) {
            bits.push(`vs ${e.compareDateRange.label}`);
        }
        return bits;
    }

    refreshChrome(): void {
        this.suggestions = this._engine.suggestions();
        this.roleSubtitle = this._engine.suggestionSubtitle();
        this.favorites = this._prefs.getFavorites();
        this.detailMode = this._prefs.getDetailMode();
    }

    clearSticky(): void {
        const ctx = this._session.context;
        if (ctx.lastEntities) {
            this._session.setContext({
                ...ctx,
                lastEntities: {
                    ...ctx.lastEntities,
                    filmId: undefined,
                    filmTitle: undefined,
                    filmTitleQuery: undefined,
                    dateRange: undefined,
                    compareDateRange: undefined,
                },
            });
        }
    }

    toggleDetailMode(): void {
        const next = this.detailMode === 'short' ? 'detailed' : 'short';
        this._prefs.setDetailMode(next);
        this.detailMode = next;
    }

    toggleFavorite(prompt: string): void {
        this.favorites = this._prefs.toggleFavorite(prompt);
    }

    copyCard(card: SlateResponseCard, event?: Event): void {
        const lines: string[] = [];
        if (card.title) lines.push(card.title);
        if (card.filterSummary) lines.push(card.filterSummary);
        if (card.body) lines.push(card.body);
        if (card.kpis?.length) {
            for (const k of card.kpis) {
                lines.push(`${k.label}: ${k.value}`);
            }
        }
        if (card.steps?.length) {
            card.steps.forEach((s, i) => lines.push(`${i + 1}. ${s}`));
        }
        this.copyText(lines.join('\n'), event);
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        this.hideOverlay();
        this.lockBodyScroll(false);
        if (this._slowTimer) clearTimeout(this._slowTimer);
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        if (!this._config.askTalkieEnabled) return;
        if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === '/' ||
                (event.shiftKey && event.key.toLowerCase() === 's'))
        ) {
            event.preventDefault();
            this.open();
            return;
        }
        if (!this.opened) return;
        if (event.key === 'Escape') {
            if (this.showPalette) {
                this.showPalette = false;
                return;
            }
            this.close();
            return;
        }
        if (event.key === 'ArrowUp' && !this.draft.trim()) {
            const hist = this._prefs.getHistory();
            if (!hist.length) return;
            event.preventDefault();
            this._histIndex = Math.min(
                this._histIndex + 1,
                hist.length - 1
            );
            this.draft = hist[this._histIndex] || '';
        }
        if (event.key === '/' && !this.draft && !event.ctrlKey && !event.metaKey) {
            const t = event.target as HTMLElement | null;
            if (t?.tagName === 'TEXTAREA') {
                event.preventDefault();
                this.showPalette = true;
            }
        }
    }

    toggle(): void {
        this.opened ? this.close() : this.open();
    }

    open(): void {
        this.opened = true;
        this.refreshChrome();
        this.showOverlay();
        this.lockBodyScroll(true);
        setTimeout(() => this.inputEl?.nativeElement?.focus(), 200);
    }

    close(): void {
        this.opened = false;
        this.showPalette = false;
        this.hideOverlay();
        this.lockBodyScroll(false);
    }

    clearChat(): void {
        this._session.clear();
        this.slowHint = false;
    }

    useSuggestion(text: string): void {
        this.showPalette = false;
        this.draft = text;
        this.send();
    }

    onEnter(event: Event): void {
        const ke = event as KeyboardEvent;
        if (ke.shiftKey) return;
        ke.preventDefault();
        this.send();
    }

    send(): void {
        const text = this.draft.trim();
        if (!text || this.busy) return;
        this.draft = '';
        this._histIndex = -1;
        this.showPalette = false;
        this._prefs.pushHistory(text);
        this._prefs.track('ask');

        const userMsg: SlateMessage = {
            id: this.uid(),
            role: 'user',
            text,
            createdAt: Date.now(),
        };
        this._session.addMessage(userMsg);

        const assistantId = this.uid();
        const assistant: SlateMessage = {
            id: assistantId,
            role: 'assistant',
            pending: true,
            thinking: ['Reading your question…'],
            thinkingDone: false,
            createdAt: Date.now(),
        };
        this._session.addMessage(assistant);
        this.busy = true;
        this.slowHint = false;
        if (this._slowTimer) clearTimeout(this._slowTimer);
        this._slowTimer = setTimeout(() => {
            if (this.busy) this.slowHint = true;
        }, 8000);
        this.scrollToBottom();

        const parsed = this._engine.parse(text);
        this.animateThinking(assistantId, parsed.thinking || []).then(() => {
            this._executor
                .execute(parsed)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (cards) => {
                        this._session.updateMessage(assistantId, {
                            pending: false,
                            thinkingDone: true,
                            cards,
                        });
                        this.busy = false;
                        this.slowHint = false;
                        this._prefs.track(
                            cards.some(
                                (c) =>
                                    c.type === 'error' || c.type === 'refuse'
                            )
                                ? 'ask_fail'
                                : 'ask_ok',
                            parsed.intentId
                        );
                        this.scrollToBottom();
                        const nav = cards.find((c) => c.autoNavigate)?.links?.[0];
                        if (nav?.route) {
                            this.openLink(nav.route, nav.queryParams);
                        }
                    },
                    error: () => {
                        this._prefs.track('ask_fail', parsed.intentId);
                        this._session.updateMessage(assistantId, {
                            pending: false,
                            thinkingDone: true,
                            cards: [
                                {
                                    type: 'error',
                                    title: 'Something went wrong',
                                    body: 'Please try again, or use Help and support if this keeps happening.',
                                    chips: [
                                        'Help and support',
                                        'Data looks wrong — Help and support',
                                        'Film views last 7 days',
                                    ],
                                },
                            ],
                        });
                        this.busy = false;
                        this.slowHint = false;
                    },
                });
        });
    }

    onChip(chip: SlateChip, card: SlateResponseCard): void {
        const prompt = slateChipPrompt(chip);
        if (card.type === 'clarify') {
            this.draft = this._executor.clarifyPrompt(prompt);
            this.send();
            return;
        }
        this.useSuggestion(prompt);
    }

    chipText(chip: SlateChip): string {
        return slateChipLabel(chip);
    }

    openLink(route: string, queryParams?: Record<string, string>): void {
        if (
            /^(mailto:|tel:|https?:\/\/|whatsapp:)/i.test(route)
        ) {
            window.open(route, '_blank', 'noopener');
            return;
        }
        this.close();
        if (queryParams && Object.keys(queryParams).length) {
            const tree = this._router.parseUrl(route);
            tree.queryParams = { ...tree.queryParams, ...queryParams };
            this._router.navigateByUrl(tree);
            return;
        }
        this._router.navigateByUrl(route);
    }

    copyText(text: string, event?: Event): void {
        event?.stopPropagation();
        if (!text) return;
        const done = () => {
            /* silent success — value already visible */
        };
        if (navigator?.clipboard?.writeText) {
            void navigator.clipboard.writeText(text).then(done).catch(() => {
                this.fallbackCopy(text);
            });
            return;
        }
        this.fallbackCopy(text);
    }

    private fallbackCopy(text: string): void {
        try {
            const ta = this._document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            this._document.body.appendChild(ta);
            ta.select();
            this._document.execCommand('copy');
            this._document.body.removeChild(ta);
        } catch {
            /* ignore */
        }
    }

    trackMsg(_: number, m: SlateMessage): string {
        return m.id;
    }

    private async animateThinking(
        messageId: string,
        steps: string[]
    ): Promise<void> {
        const shown: string[] = [];
        for (const step of steps) {
            shown.push(step);
            this._session.updateMessage(messageId, {
                thinking: [...shown],
                thinkingDone: false,
                pending: true,
            });
            await this.delay(280);
        }
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private scrollToBottom(): void {
        setTimeout(() => {
            const el = this.threadRef?.nativeElement;
            if (el) el.scrollTop = el.scrollHeight;
        }, 40);
    }

    private uid(): string {
        return `slate_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    }

    private showOverlay(): void {
        if (this._overlay) return;
        this._overlay = this._document.createElement('div');
        this._overlay.classList.add('slate-overlay');
        this._overlay.addEventListener('click', () => this.close());
        this._document.body.appendChild(this._overlay);
    }

    private hideOverlay(): void {
        if (!this._overlay) return;
        this._overlay.remove();
        this._overlay = undefined;
    }

    private lockBodyScroll(lock: boolean): void {
        const body = this._document.body;
        if (!body) return;
        if (lock) {
            body.style.overflow = 'hidden';
            body.classList.add('slate-scroll-lock');
        } else {
            body.style.overflow = '';
            body.classList.remove('slate-scroll-lock');
        }
    }
}

